import React, { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { TakeFiveRiskTypeEnum, TakeFiveRiskTypeEnumToString } from "../../../shared/enums/take-five-risk-type-enum";
import { Hazard, HazardImage, HazardLookup, NewTaskTakeFive, RiskLookup } from "../../../shared/models/api/models";
import { getHazards, getRisks } from "../../../shared/services/railOperationalFormsApi";
import { ToolTipButton } from "../../menu/Labels";
import { HazardChoice, HazardRiskChoice, MultilineTextInput, RiskTypeChoices } from "../forms/FormControls";
import Modal from "./Modal";
import { RiskScore } from "./RiskScore";
import './TakeFive.css';
import {
    fieldInvalid,
    handleChange,
    takeFiveFormState as t,
    TakeFiveFormState,
    enableValidation,
    resetValidation
} from "./TakeFiveFormState";
import TaskTakeFiveQuestions from "./TakeFiveQuestions";

const ImageFileUploadSection = (props: { form: NewTaskTakeFive, onFileChange: (e: any) => void, fileError?: string }) => {
    const existingFile = props.form.file ? props.form.file.name : undefined;
    const fileInputRef = React.useRef<any>(null);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const FileInput = () => (
        <FormGroup>
            <div>
                <Label for="documentUpload">Select photo: <span style={{ fontStyle: 'italic' }}>(You can only attach .jpg, .jpeg or .png files)</span></Label>
                <Input
                    innerRef={fileInputRef}
                    type="file"
                    name="file"
                    className={`d-none ${false ? 'invalid-field' : ''}`}
                    key="documentupload"
                    id="documentUpload"
                    accept=".jpeg,.jpg,.png"
                    onChange={props.onFileChange} />
                <div className="browse-button" onClick={handleBrowseClick} style={{ cursor: 'pointer' }}>Browse</div>
            </div>
            {props.fileError && <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>{props.fileError}</div>}
        </FormGroup>
    );

    return (
        <>
            <h3>File upload</h3>
            {
                existingFile ?
                    <> <Label>Selected file:&nbsp;{existingFile} </Label> <Button className="btn-secondary" onClick={() => props.onFileChange(undefined)}> Remove </Button></> :
                    <FileInput />
            }
        </>
    );
};

const toolTip = {
    riskType: <><b>Material</b><br />
        A risk that can be measured and quantified, such as a chemical hazard or physical hazard<br /><br />
        <b>Non-Material</b><br />
        A risk that is not easily measured or quantified, such as a psychological hazard or ergonomic hazard</>,
    hazards:<><b>When to Add Hazards:</b><br />
        Hazards must be added and completed when:<br /><br />
        <b>Section 1 - Think Through The Task:</b><br />
        Any question is answered with <b>No</b> (such as: task scope not understood, no procedure exists, task steps not understood, tools not in good order, incorrect PPE,<br />no necessary permits, plan not clear, risk to hands cannot be eliminated)<br /><br />
        <b>Section 2 - Spot The Hazards:</b><br />
        Any question is answered with <b>Yes</b> (such as: hands in vicinity of equipment, equipment guards removed, mobile equipment risk present, hazardous materials involved,<br />task performed without isolation procedure, working at heights, impacted by other activity, environmental H&S hazards present)<br /><br />
        If either condition is met, at least one hazard must be added before the Take 5 can be submitted.</>
}

interface HazardFormProps {
    open: boolean;
    onClose: () => void;
    editingHazard?: Hazard | null;
}

export const HazardForm = (props: HazardFormProps) => {
    const q = TaskTakeFiveQuestions;
    const [form, setForm] = useState({} as NewTaskTakeFive);
    const [hazardImage, setHazardImage] = useState({} as HazardImage);
    const [updateForm, setUpdateForm] = useState(false);
    const [hazardList, setHazardList] = useState([] as Hazard[]);
    const [score, setScore] = useState('');
    const [hazardLookup, setHazardLookup] = useState([] as HazardLookup[]);
    const [riskLookup, setRiskLookup] = useState([] as RiskLookup[]);
    const [loading, setLoading] = useState(false);
    const [formattedScore, setFormattedScore] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('' as string | undefined)
    const [riskTag, setRiskTag] = useState('' as string | undefined);
    const [showErrors, setShowErrors] = useState(false);
    const [riskTypeDesc, setRiskTypeDesc] = useState('' as string | undefined);
    const [fileError, setFileError] = useState('' as string);
    const [isEditing, setIsEditing] = useState(false);
    const isInitializedRef = React.useRef(false);

    // Clear form state when component mounts for a new hazard (modal opens)
    // Since Modal unmounts when open=false, this runs fresh each time modal opens
    useEffect(() => {
        if (!props.editingHazard) {
            // Reset global form state
            t.setState(state => {
                state.form.hazard = undefined;
                state.form.otherHazard = undefined;
                state.form.riskType = undefined;
                state.form.risk = undefined;
                state.form.control = undefined;
                state.form.file = undefined;
                state.form.fileName = undefined;
                state.form.score = undefined;
                state.form.bGroundColor = undefined;
                state.form.riskTag = undefined;
            });
            // Reset local UI state
            setHazardImage({});
            setFormattedScore('');
            setBackgroundColor('');
            setRiskTag('');
            setFileError('');
            setShowErrors(false);
            setScore('');
            setIsEditing(false);
            isInitializedRef.current = false;
        }
    }, [props.editingHazard]);

    // Modal
    const [open, setOpen] = useState(false);
    const showModal = () => setOpen(true);
    const hideModal = () => setOpen(false);

    // Initialize edit mode when editingHazard changes
    useEffect(() => {
        if (props.editingHazard && !isInitializedRef.current) {
            isInitializedRef.current = true;
            setIsEditing(true);
            t.setState(s => {
                s.form.hazard = props.editingHazard!.hazardId;
                s.form.otherHazard = props.editingHazard!.otherHazard;
                s.form.riskType = props.editingHazard!.riskType;
                s.form.risk = props.editingHazard!.riskId;
                s.form.control = props.editingHazard!.control;
                s.form.score = props.editingHazard!.riskScore?.toString();
                s.form.file = props.editingHazard!.file;
                s.form.bGroundColor = (props.editingHazard! as any).bGroundColor;
                s.form.riskTag = (props.editingHazard! as any).riskTag;
            });
            if (props.editingHazard.file) {
                setHazardImage({
                    file: props.editingHazard.file,
                    fileName: props.editingHazard.file.name
                });
            }
            // Update UI with risk score info
            if (props.editingHazard.riskScore) {
                setFormattedScore('(' + props.editingHazard.riskScore + ')');
            }
            // Update UI with background color and risk tag
            setBackgroundColor((props.editingHazard as any).bGroundColor);
            setRiskTag((props.editingHazard as any).riskTag);
        }
    }, [props.editingHazard]);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            setLoading(true);
            const hazards = await getHazards();
            setHazardLookup(hazards);
            const risks = await getRisks();
            setRiskLookup(risks);
            setLoading(false);
        };

        mount();
        return () => { mounted = false };
    }, []);

    useEffect(() => {
        let isProcessing = false;
        
        const sub = t.subscribe((s: TakeFiveFormState) => {
            setForm(s.form);

            if (updateForm && !isProcessing) {
                isProcessing = true;
                
                if (typeof s.form.score === 'string') {
                    setScore(s.form.score);
                }

                const hazard = {
                    hazardId: s.form.hazard,
                    hazardDetail: hazardLookup?.filter(h => h.id === s.form.hazard).map(d => d.hazard).toString(),
                    otherHazard: s.form.otherHazard,
                    riskType: s.form.riskType,
                    riskId: s.form.risk,
                    riskDetail: riskLookup?.filter(r => r.id === s.form.risk).map(d => d.risk).toString(),
                    control: s.form.control,
                    riskScore: s.form.score,
                    file: hazardImage.file,
                    bGroundColor: s.form.bGroundColor,
                    riskTag: s.form.riskTag
                } as Hazard

                // Save/update hazard and reset form in one operation
                t.setState(state => {
                    if (isEditing && props.editingHazard) {
                        // Update existing hazard
                        const index = state.form.taskHazards?.indexOf(props.editingHazard!);
                        if (index !== undefined && index >= 0 && state.form.taskHazards) {
                            state.form.taskHazards[index] = hazard;
                        }
                    } else {
                        // Add new hazard
                        if (!state.form.taskHazards) {
                            state.form.taskHazards = [];
                        }
                        state.form.taskHazards.push(hazard);
                    }

                    // Reset form fields
                    state.form.hazard = undefined;
                    state.form.otherHazard = undefined;
                    state.form.riskType = undefined;
                    state.form.risk = undefined;
                    state.form.control = undefined;
                    state.form.file = undefined;
                    state.form.fileName = undefined;
                    state.form.score = undefined;
                    state.form.bGroundColor = undefined;
                    state.form.riskTag = undefined;
                });

                setUpdateForm(false);
                setHazardImage({});
                setIsEditing(false);
                setFormattedScore('');
                setBackgroundColor('');
                setRiskTag('');

                // Close modal after a micro-task to ensure state updates complete
                setTimeout(() => {
                    resetValidation();
                    props.onClose();
                }, 0);
            }
        });

        return () => { sub.unsubscribe(); };
    }, [updateForm, isEditing, props.editingHazard, hazardLookup, riskLookup, hazardImage, props]);

    const handleFileChange = (event: any) => {
        if (event) {
            const uploadedFile = event.target.files[0];
            const acceptedExtensions = ['.jpeg', '.jpg', '.png'];
            const fileExtension = '.' + uploadedFile.name.split('.').pop().toLowerCase();
            
            if (!acceptedExtensions.includes(fileExtension)) {
                setFileError('Only .jpeg, .jpg, and .png files are accepted');
                return;
            }
            
            setFileError('');
            setHazardImage({
                file: uploadedFile,
                fileName: uploadedFile!.name
            });
            t.setState(s => s.form.file = uploadedFile);
        } else {
            setFileError('');
            setHazardImage({
                file: undefined,
                fileName: undefined
            });
            t.setState(s => s.form.file = undefined);
        }
    }

    const saveHazardAndControls = () => {
        // Check if all required fields are filled (directly check form values)
        const hazardEmpty = !form.hazard || form.hazard === undefined;
        const riskTypeEmpty = !form.riskType || form.riskType === undefined;
        const riskEmpty = !form.risk || form.risk === undefined;
        const controlEmpty = !form.control || form.control === undefined || form.control.trim() === '';
        const riskScoreEmpty = !form.score || form.score === undefined;
        
        // If any field is empty, show errors and enable validation
        if (hazardEmpty || riskTypeEmpty || riskEmpty || controlEmpty || riskScoreEmpty) {
            setShowErrors(true);
            enableValidation();
        } else {
            // All fields are valid, proceed with save
            setUpdateForm(true);
        }
    };

    useEffect(() => {
        const sub = t.subscribe((s: TakeFiveFormState) => {
            if (typeof s.form.score === 'string' && s.form.score) {
                setFormattedScore('(' + s.form.score + ')');
                setBackgroundColor(s.form.bGroundColor?.toString());
                setRiskTag(s.form.riskTag);
            } else {
                // If score is undefined or not a string, clear the display
                setFormattedScore('');
                setBackgroundColor('');
                setRiskTag('');
            }
        });

        return () => { sub.unsubscribe(); };
    }, []);

    // Sync display state when form changes (for edit mode initialization)
    useEffect(() => {
        if (form.score && typeof form.score === 'string') {
            setFormattedScore('(' + form.score + ')');
            setBackgroundColor(form.bGroundColor?.toString());
            setRiskTag(form.riskTag);
        }
    }, [form.bGroundColor, form.riskTag, form.score]);

    const resetScore = () => {
        t.setState((s: TakeFiveFormState) => {
            s.form.score = undefined;
            s.form.bGroundColor = undefined;
            s.form.riskTag = undefined;
        });
        setFormattedScore('');
        setBackgroundColor('');
        setRiskTag('');
        setShowErrors(false);
    };

    useEffect(() => {
        if (form.riskType && +form.riskType === TakeFiveRiskTypeEnum.Material) {
            setRiskTypeDesc(TakeFiveRiskTypeEnumToString(TakeFiveRiskTypeEnum.Material));
        } else if (form.riskType && +form.riskType === TakeFiveRiskTypeEnum.NonMaterial) {
            setRiskTypeDesc(TakeFiveRiskTypeEnumToString(TakeFiveRiskTypeEnum.NonMaterial));
        }
    }, [form.riskType]);

    return (
        <div className="tf-list-container">
            <div className="main-form-header">Hazard and Controls</div>

            <div className="main-form-row row-divider main-form-row-view">
                <div className="question">{q.hazard.value}<ToolTipButton text={toolTip.hazards} /></div>
                <div className="answer">
                    <HazardChoice
                        name={q.hazard.key}
                        onAnswerUpdate={handleChange(q.hazard.key)}
                        invalid={fieldInvalid(q.hazard.key) || (showErrors && (!form.hazard || form.hazard === undefined))} />
                </div>
            </div>

            <div className="main-form-row row-divider main-form-row-view">
                <div className="question">{q.riskType.value}<ToolTipButton text={toolTip.riskType} /></div>
                <div className="answer">
                    <RiskTypeChoices
                        name={q.riskType.key}
                        onAnswerUpdate={handleChange(q.riskType.key)}
                        invalid={fieldInvalid(q.riskType.key) || (showErrors && (!form.riskType || form.riskType === undefined))} />
                </div>
            </div>

            <div className="main-form-row row-divider main-form-row-view">
                {form.riskType !== undefined ?
                    <div className="question">{riskTypeDesc}</div>
                    :
                    <div className="question-info question">{q.risk.value}</div>
                }
                <div className="answer">
                    <HazardRiskChoice
                        name={q.risk.key}
                        onAnswerUpdate={handleChange(q.risk.key)}
                        invalid={fieldInvalid(q.risk.key) || (showErrors && (!form.risk || form.risk === undefined))}
                        riskType={form.riskType}
                        selectedRiskId={form.risk} />
                </div>
            </div>

            <div className="main-form-row row-divider">
                <div className="question">{q.control.value}</div>
                <div className="answer">
                    <MultilineTextInput
                        onAnswerUpdate={handleChange(q.control.key)}
                        invalid={fieldInvalid(q.control.key) || (showErrors && (!form.control || form.control === undefined || form.control.trim() === ''))}
                    />
                </div>
            </div>

            <div className="main-form-row main-form-row-view row-divider">
                <div className="risk-score">
                    <div className="title">
                        {q.riskScore.value}
                    </div>
                    <div className="score" style={{ backgroundColor }}>{riskTag} {formattedScore}</div>
                    {showErrors && (!form.score || form.score === undefined) && (
                        <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>Risk Score is required</div>
                    )}
                </div>

                <Modal open={open} onClose={hideModal} maxWidth={"75%"}>
                    <RiskScore open={open} onClose={hideModal}></RiskScore>
                </Modal>

                <div className="risk-score-btn">
                    <Button className="btn-primary" onClick={() => { setOpen(true) }}>Get Risk Score</Button>
                </div>
                <div className="file-upload">
                    <ImageFileUploadSection form={form} onFileChange={handleFileChange} fileError={fileError}></ImageFileUploadSection>
                </div>
            </div>

            <div className="submit btns-container">
                <Button className="btn-primary" style={{ marginLeft: '1rem' }} onClick={() => { saveHazardAndControls(); }}>{isEditing ? 'Update Hazard' : 'Save Hazard'}</Button>
                <Button className="btn-primary btn-spacing" onClick={() => { resetScore(); resetValidation(); props.onClose(); }}>Cancel</Button>
            </div>
        </div>
    );
}